import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Select } from "@onshopfront/components";
import { type ThunkDispatch } from "redux-thunk";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { showSafeDropConfirm } from "../../../../Actions/SellScreen/SellScreenActions";
import Button from "../../../../Common/Buttons/Button";
import NoTranslate from "../../../../Common/NoTranslate";
import PopupManager from "../../../../Common/Popups/PopupManager";
import TouchNumbers from "../../../../Common/TouchNumbers/TouchNumbers";
import { type InjectedDataSourceProps, withDataSource } from "../../../../Data/DataStores/WithDataSource";
import { currency } from "../../../../Utilities/Generic/Numbers";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";

const connector = connect();

interface Props extends ConnectedProps<typeof connector>, InjectedDataSourceProps {
    dispatch: ThunkDispatch<any, any, any>;
    callback?: () => void;
}

interface State {
    loading: boolean;
    cashInDrawer: number;
    amount: string;
    paymentMethod: null | {
        label: string;
        value: string;
    };
    paymentMethods: Array<{
        label: string;
        value: string;
    }>;
}

class SafeDropFormDialog extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loading       : true,
            cashInDrawer  : 0,
            amount        : "",
            paymentMethod : null,
            paymentMethods: [],
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleCompleteClick = this.handleCompleteClick.bind(this);
        this.handleMethodChange = this.handleMethodChange.bind(this);
        this.loadTakings = this.loadTakings.bind(this);
    }

    public componentDidMount(): void {
        this.loadTakings();
    }

    protected loadTakings(): Promise<void> {
        this.setState({
            loading: true,
        });

        return this.props.data
            .then(source => {
                const promises = [];

                promises.push(
                    source.takings.getByType("cash")
                        .then(takings => {
                            let cashInDrawer = 0;

                            for(let i = 0, l = takings.length; i < l; i++) {
                                cashInDrawer += takings[i].amount;
                            }

                            this.setState({
                                cashInDrawer: cashInDrawer,
                            });
                        })
                );

                promises.push(
                    source.paymentMethods.getByType("cash")
                        .then(methods => {
                            const paymentMethods = [];
                            let paymentMethod = null;

                            if(methods.length === 1) {
                                paymentMethod = {
                                    label: "",
                                    value: methods[0].uuid,
                                };
                            }

                            for(let i = 0, l = methods.length; i < l; i++) {
                                paymentMethods.push({
                                    label: methods[i].name,
                                    value: methods[i].uuid,
                                });
                            }

                            this.setState({
                                paymentMethod,
                                paymentMethods,
                            });
                        })
                );

                return Promise.all(promises);
            })
            .then(() => {
                this.setState({
                    loading: false,
                });
            });
    }

    protected handleChange(value: string): void {
        this.setState({
            amount: value,
        });
    }

    protected handleMethodChange(option: { value: string; label: string; }): void {
        this.setState({
            paymentMethod: option,
        });
    }

    protected handleCancelClick(): void {
        this.props.dispatch(hideDialog());
    }

    protected handleCompleteClick(): void {
        const amount = parseFloat(this.state.amount);

        if(isNaN(amount) || amount <= 0) {
            PopupManager.error("The amount provided is invalid");

            return;
        }

        if(!this.state.paymentMethod) {
            PopupManager.error("Please select a payment method");

            return;
        }

        this.props.dispatch(showSafeDropConfirm(amount, this.state.paymentMethod.value, this.props.callback));
    }

    protected handleConfirm(value: string): void {
        const parsed = parseFloat(value);

        if(isNaN(parsed)) {
            PopupManager.error("The amount provided is invalid");

            return;
        }

        if(parsed > 9999) {
            PopupManager.info("Please press the complete button to confirm");

            return;
        }

        this.handleCompleteClick();
    }

    public render(): React.ReactNode {
        let methods = null;

        if(this.state.paymentMethods.length > 1) {
            methods = (
                <React.Fragment>
                    <p>Select which payment method to apply the safe drop to?</p>
                    <Select
                        value={this.state.paymentMethod}
                        options={this.state.paymentMethods}
                        onChange={this.handleMethodChange}
                    />
                </React.Fragment>
            );
        }

        return (
            <div className="safe-drop-form">
                <p>
                    Received cash in drawer: {
                        this.state.loading ?
                            <FontAwesomeLight spin icon="spinner-third" /> :
                            <NoTranslate>{currency(this.state.cashInDrawer)}</NoTranslate>
                    }
                </p>
                {methods}
                <p>How much would you like to drop into safe?</p>
                <TouchNumbers
                    static
                    hideConfirm
                    bounds={{
                        min: 0,
                        max: 999999.99,
                    }}
                    input="currency"
                    value={this.state.amount}
                    onChange={this.handleChange}
                    onConfirm={this.handleConfirm}
                />
                <div className="buttons">
                    <Button classes={[ "button-cancel" ]} onClick={this.handleCancelClick}>Cancel</Button>
                    <Button classes={[ "button-add" ]} onClick={this.handleCompleteClick}>Complete</Button>
                </div>
            </div>
        );
    }
}

export default connector(withDataSource(SafeDropFormDialog));
